import keyBy from 'lodash/keyBy';
import { AlertOutlined, ExperimentOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { activityAgentRoutes } from '@tools/activity-agent-ui';
import { busyRoutes } from '@tools/busy-ui';
import { communicationPlatformRoutes } from '@tools/communication-platform-ui';
import { cryptoServiceRoutes } from '@tools/crypto-service-ui';
import { featureStrategyBuilderRoutes } from '@tools/feature-strategy-builder-ui';
import { feesManagementRoutes } from '@tools/fees-management-ui';
import { gayaRoutes } from '@tools/gaya-ui';
import { incidentManagementRoutes } from '@tools/incident-management-ui';
import { luciRoutes, SearchIcon } from '@tools/luci-ui';
import { melioLlmPlaygroundRoutes } from '@tools/melio-llm-playground';
import { ExtraComponent, pathFinderRoutes } from '@tools/path-finder-ui';
import { recoRoutes } from '@tools/reco-ui';
import { rubyRoutes } from '@tools/robinhood-ui';
import { settingsRoutes } from '@tools/settings-ui';
import { AppNames } from '@tools/shared';
import { sherlockRoutes } from '@tools/sherlock-ui';
import { mail, recoIcon, sherlockIcon } from '@tools/ui/assets';
import { config } from '@tools/ui/consts';
/* PLOP_INJECT_IMPORT */
import { ColorModule } from '@tools/ui/enums';
import { AppRouter, ModuleRouter, PageRouter, ProtectedRoutesMap, TModules } from '@tools/ui/types';
import { WatsonIcon, watsonRoutes } from '@tools/watson-ui';
import { xeroTestAppRoutes } from '@tools/xero-test-app-ui';

export const legacyRoutesMap = {
  '/inbox/incoming-payments': '/sherlock/general/live',
} as const;

const dataModules: TModules[] = [
  {
    name: 'Xero Test App',
    path: '/xero-test-app',
    icon: SearchIcon,
    routes: xeroTestAppRoutes as AppRouter,
    color: ColorModule.DEFAULT,
  },
  {
    name: AppNames.MelioLlmPlayground,
    path: '/melio-llm-playground',
    icon: ExperimentOutlined,
    routes: melioLlmPlaygroundRoutes as AppRouter,
    color: ColorModule.DEFAULT,
  },
  {
    name: AppNames.Sherlock,
    path: '/sherlock',
    icon: sherlockIcon,
    routes: sherlockRoutes as AppRouter,
    color: ColorModule.SHERLOCK,
  },
  {
    name: AppNames.PathFinder,
    path: '/path-finder',
    icon: SearchIcon,
    routes: pathFinderRoutes as AppRouter,
    extraComponent: ExtraComponent,
    color: ColorModule.PATH_FINDER,
  },
  {
    name: AppNames.Ruby,
    path: '/ruby',
    icon: SearchIcon,
    routes: rubyRoutes as AppRouter,
    color: ColorModule.RUBY,
  },
  {
    name: AppNames.FeeManagement,
    path: '/fees-management',
    icon: SearchIcon,
    routes: feesManagementRoutes as AppRouter,
    color: ColorModule.DEFAULT,
  },
  {
    name: AppNames.CryptoService,
    path: '/crypto-service',
    icon: SearchIcon,
    routes: cryptoServiceRoutes as AppRouter,
    color: ColorModule.DEFAULT,
  },
  {
    name: AppNames.FeatureStrategyBuilder,
    path: '/feature-strategy-builder',
    icon: SearchIcon,
    routes: featureStrategyBuilderRoutes as AppRouter,
    color: ColorModule.STRATEGY_BUILDER,
  },
  {
    name: AppNames.Watson,
    path: '/watson',
    icon: WatsonIcon,
    routes: watsonRoutes as AppRouter,
    color: ColorModule.WATSON,
  },

  {
    name: AppNames.Reco,
    path: '/reco',
    icon: recoIcon,
    routes: recoRoutes as AppRouter,
    color: ColorModule.RECO,
  },
  {
    name: AppNames.Settings,
    path: '/settings',
    icon: UnorderedListOutlined,
    hideFromMenu: true,
    routes: settingsRoutes,
    color: ColorModule.SETTINGS,
  },
  {
    name: AppNames.Luci,
    path: '/luci',
    icon: SearchIcon,
    routes: luciRoutes as AppRouter,
    color: ColorModule.LUCI,
  },

  {
    name: AppNames.Busy,
    path: '/busy',
    icon: SearchIcon, // TODO Yoni change icon for busy
    routes: busyRoutes,
    color: ColorModule.BUSY,
  },
  {
    name: AppNames.Gaya,
    path: '/gaya',
    icon: SearchIcon,
    routes: gayaRoutes as AppRouter,
    color: ColorModule.GAYA,
  },
  {
    name: AppNames.CommPlatform,
    path: '/communication-platform',
    icon: mail,
    routes: communicationPlatformRoutes as AppRouter,
    color: ColorModule.COMM,
  },
  {
    name: AppNames.IncidentManagement,
    path: '/incident-management',
    icon: AlertOutlined,
    routes: incidentManagementRoutes as AppRouter,
    color: ColorModule.INCIDENT,
  },
  {
    name: AppNames.ActivityAgent,
    path: '/activity-agent',
    icon: SearchIcon,
    routes: activityAgentRoutes as AppRouter,
    color: ColorModule.DEFAULT,
  },
];

export const isModuleOpen = (module: TModules): boolean =>
  config.isLocalMode || config.isStagingMode || Boolean(!module.devModeOnly);

const getModuleRoutes = (
  routes: (PageRouter | ModuleRouter)[],
  prevRoute: PageRouter | ModuleRouter = {} as ModuleRouter,
  prevObjectPath,
) => {
  return [
    ...routes
      .map((route, idx) => {
        const { pages = [], tabs = [] } = route as PageRouter & ModuleRouter;
        if (pages.length) {
          return getModuleRoutes(pages, route, `${prevObjectPath}[${idx}].pages`);
        }

        if (tabs.length) {
          return getModuleRoutes(
            tabs as unknown as PageRouter[],
            { ...route, path: `${prevRoute.path}${route.path}` },
            `${prevObjectPath}[${idx}]tabs`,
          );
        }
        return [
          {
            name: prevRoute?.name ? `[${prevRoute?.name}] ${route.name}` : route.name,
            path: prevRoute?.name ? `${prevRoute?.path}${route.path}` : route.path,
            objectPath: `${prevObjectPath}[${idx}]`,
          },
        ];
      })
      .flat(2),
  ];
};

const getPages = (routes: TModules[]) => {
  return routes.map((route, idx) => {
    const isModuleRoute = (route.routes as AppRouter)?.modules;

    const moduleRoutes = isModuleRoute
      ? (route.routes as AppRouter)?.modules?.slice(1)
      : (route.routes as unknown as AppRouter['modules'])?.slice(1);

    return {
      name: route.name,
      path: route.path,
      routes: getModuleRoutes(
        moduleRoutes as unknown as PageRouter[],
        {
          path: route.path,
        } as PageRouter,
        '',
      ),
    };
  });
};

export const permissionModule = dataModules.reduce(
  (acc, module) => ({ ...acc, [module.name]: module.name }),
  {},
);
export const moduleRoutesMap = keyBy(
  getPages(dataModules),
  'name',
) as unknown as ProtectedRoutesMap;

export { dataModules };
